import { getReq, postReq, putReq, deleteReq } from "../utils/request";
import {
    OrganizationRating,
    OrganizationRatingSearchCriteria,
    RatingContextType,
    RatingDimensionCategory,
    RatingDimensionConfig,
    RatingType,
} from "../types/Rating";

/**
 * API helpers for the rating system. All calls go through the shared request utils
 * so JWT handling, error mapping, and 401 retry are consistent.
 */

/* --------------- rating dimensions (admin-managed) --------------- */

export const fetchDimensionsByType = async (
    ratingType: RatingType,
    includeInactive: boolean = false,
    category?: RatingDimensionCategory,
): Promise<RatingDimensionConfig[]> => {
    const params = new URLSearchParams({ includeInactive: String(includeInactive) });
    if (category) params.set("category", category);
    const response = await getReq(
        `rating-dimensions/by-type/${ratingType}?${params.toString()}`,
    );
    return response.status === 200 ? response.body : [];
};

export const fetchDimensionById = async (id: number): Promise<RatingDimensionConfig | null> => {
    const response = await getReq(`rating-dimensions/${id}`);
    return response.status === 200 ? response.body : null;
};

export const createDimension = async (dim: Partial<RatingDimensionConfig>) =>
    postReq("rating-dimensions", dim);

export const updateDimension = async (id: number, dim: Partial<RatingDimensionConfig>) =>
    putReq(`rating-dimensions/${id}`, dim);

export const deleteDimension = async (id: number) =>
    deleteReq(`rating-dimensions/${id}`);

export const restoreDimension = async (id: number) =>
    postReq(`rating-dimensions/${id}/restore`, {});

/* --------------- organization ratings --------------- */

export const searchRatings = async (
    criteria: OrganizationRatingSearchCriteria,
): Promise<{ data: OrganizationRating[]; total: number }> => {
    const response = await postReq("organization-ratings/search", criteria);
    if (response.status === 200) {
        return response.body;
    }
    return { data: [], total: 0 };
};

export const fetchRatingsByContext = async (
    contextType: RatingContextType,
    contextId: number,
    ratingType?: RatingType,
): Promise<OrganizationRating[]> => {
    const url = ratingType
        ? `organization-ratings/context/${contextType}/${contextId}?ratingType=${ratingType}`
        : `organization-ratings/context/${contextType}/${contextId}`;
    const response = await getReq(url);
    return response.status === 200 ? response.body : [];
};

export const fetchUnratedMissions = async (): Promise<number[]> => {
    const response = await getReq("organization-ratings/unrated-missions");
    return response.status === 200 ? response.body : [];
};

export const createRating = async (rating: Partial<OrganizationRating>) =>
    postReq("organization-ratings", rating);

export const createRatingsBulk = async (ratings: Partial<OrganizationRating>[]) =>
    postReq("organization-ratings/bulk", ratings);

export const updateRating = async (id: number, rating: Partial<OrganizationRating>) =>
    putReq(`organization-ratings/${id}`, rating);

export const deleteRating = async (id: number) =>
    deleteReq(`organization-ratings/${id}`);

export const restoreRating = async (id: number) =>
    postReq(`organization-ratings/${id}/restore`, {});

/* --------------- intelligence (Phase 4) --------------- */

export const fetchTopProviders = async (limit: number = 10, minRatings: number = 1) => {
    const response = await getReq(
        `organization-ratings/top-providers?limit=${limit}&minRatings=${minRatings}`,
    );
    return response.status === 200 ? response.body : [];
};

export const fetchClientsNeedingAttention = async (threshold: number = 2.5, limit: number = 20) => {
    const response = await getReq(
        `organization-ratings/clients-needing-attention?threshold=${threshold}&limit=${limit}`,
    );
    return response.status === 200 ? response.body : [];
};

export const fetchOrganizationTrend = async (
    organizationId: number,
    ratingType: RatingType,
    months: number = 12,
) => {
    const response = await getReq(
        `organization-ratings/trend/${organizationId}/${ratingType}?months=${months}`,
    );
    return response.status === 200 ? response.body : [];
};

export const fetchDimensionBreakdown = async (organizationId: number, ratingType: RatingType) => {
    const response = await getReq(
        `organization-ratings/dimension-breakdown/${organizationId}/${ratingType}`,
    );
    return response.status === 200 ? response.body : [];
};
